<script lang="ts">
  import { onMount } from "svelte";
  // components
  import CreditsTable from "@retailer/svelte/Payments/Credits/components/Table.svelte";
  import AvailableCredits from "@retailer/svelte/Payments/Credits/components/AvailableCredits.svelte";
  import DatePicker from "@shared/svelte/Components/DatePicker.svelte";
  import PaginationLarge from "@shared/svelte/Components/Pagination/Large.svelte";
  import CreditsBannerSelector from "@retailer/svelte/Payments/Banners/selectors/CreditsBannerSelector.svelte";
  import FilterDropdown from "@retailer/svelte/Payments/Credits/components/FilterDropdown.svelte";
  import MultiSearchAndSelectDropdown from "@shared/svelte/Components/MultiSearchAndSelectDropdown.svelte";

  // services
  import * as CreditsService from "@retailer/services/credits_service";

  // stores
  import { creditsStore } from "@retailer/stores/CreditsStore";

  // types
  import type { CreditIndexParams } from "@retailer/services/credits_service";

  // data

  let distributors = [];
  let selectedDistributors = [];
  let distributorSearchValue = "";

  let filters: CreditIndexParams = {
    search: "",
    page: $creditsStore.pagination.page.toString() || "1",
    start_date: undefined,
    end_date: undefined,
    status: undefined,
    distributor_ids: undefined,
  };
  const statusDropDownData = [
    {
      text: "All Statuses",
      value: undefined,
    },
    {
      text: "Available",
      value: "available",
    },
    {
      text: "Scheduled",
      value: "scheduled",
    },
    {
      text: "Pending",
      value: "pending",
    },
    {
      text: "Applied",
      value: "applied",
    },
    {
      text: "Closed",
      value: "closed",
    },
  ];

  // functions
  const fetchCredits = (page: number) => {
    filters.page = page.toString();
    CreditsService.index(filters);
  };

  const onStatusSelect = (selectedStatus: CreditsService.CreditStatus) => {
    filters.status = selectedStatus;
    fetchCredits(1);
  };

  const fetchDistributors = async () => {
    try {
      distributors = await CreditsService.fetchDistributorsForCredits();
    } catch (error) {
      console.error("Error fetching distributors:", error);
    }
  };

  const handleDistributorSelection = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const selectedValue = Number(target.value);

    if (target.checked) {
      if (!selectedDistributors.includes(selectedValue)) {
        selectedDistributors = [...selectedDistributors, selectedValue];
      }
    } else {
      selectedDistributors = selectedDistributors.filter(id => id !== selectedValue);
    }

    filters.distributor_ids = selectedDistributors.join(",");
    fetchCredits(1);
  };

  const handleDistributorSearch = (event: Event) => {
    const inputElement = event.target as HTMLInputElement;
    const searchValue = inputElement.value.toLowerCase().trim();

    distributorSearchValue = searchValue;

    if (!searchValue) {
      fetchDistributors();
    } else {
      distributors = distributors.filter(({ name }) => name.toLowerCase().includes(searchValue));
    }
  };

  const handleDistributorClear = () => {
    selectedDistributors = [];
    fetchDistributors();
    fetchCredits(1);
  };

  onMount(() => {
    fetchDistributors();
    fetchCredits(1);
  });

  // reactives
  $: pagination = {
    ...$creditsStore.pagination,
    goToPage: nextPageNum => ($creditsStore.pagination.page = nextPageNum),
  };

  // Extracting page into it's own variable keeps the fetch reactive below from looping
  $: page = pagination.page;
  $: {
    CreditsService.getAvailable();
    fetchCredits(page);
  }
</script>

<div class="fizz-expand-32-on-large fizz-expand-16-on-small" data-testid="credits-index">
  <CreditsBannerSelector />
  <AvailableCredits />
  <div class="fizz-flex-row-responsive fizz-inset-squish-32">
    <div>
      <input
        type="text"
        name="search"
        data-testid="credits-search-input"
        placeholder="Search Credits"
        class="fizz-input-search fizz-full-width"
        style="min-width: 230px"
        bind:value={filters.search}
        on:keyup={_ => fetchCredits(1)}
      />
    </div>
    <div class="grid-row">
      <div class="grid-col c12-c12 fizz-styles">
        <MultiSearchAndSelectDropdown
          minWidth={"20rem"}
          options={distributors}
          defaultButtonLabel="All Distributors"
          object="distributor"
          searchValue={distributorSearchValue}
          onCheckboxChange={handleDistributorSelection}
          onDistributorSearch={handleDistributorSearch}
          onClear={handleDistributorClear}
          bind:selectedValues={selectedDistributors}
          alignmentOptions={{ desktop: { xPosition: "left" }, mobile: { xPosition: "left" } }}
          spacing={"space-y-4"}
          showSelectAll={true}
        />
      </div>
    </div>
    <div>
      <DatePicker
        bind:startDate={filters.start_date}
        bind:endDate={filters.end_date}
        defaultButtonLabel={"All Credit Dates"}
        runFilters={() => fetchCredits(1)}
        minWidth={"100%"}
        extraLabelClasses={"fizz-text-strong"}
        selectButtonStyle
        alignmentOptions={{ desktop: { xPosition: "left" }, mobile: { xPosition: "left" } }}
      />
    </div>
    <div>
      <FilterDropdown
        name="creditStatusDropdown"
        options={statusDropDownData}
        onSelect={onStatusSelect}
        extraClasses="fizz-full-width"
      />
    </div>
  </div>
  <table
    class="fizz-table fizz-stack-12"
    class:fizz-table-highlight-rows={$creditsStore.credits.length > 0 && $creditsStore.loaded.index}
  >
    <CreditsTable credits={$creditsStore.credits} />
  </table>
  {#if $creditsStore.credits.length > 0}
    <div class="fizz-stack-12">
      <PaginationLarge {pagination} />
    </div>
  {/if}
</div>
